'use client';

import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DateSelectorSheet from '../date-selector-sheet';
import Input from '../internal/input';
import type { PopoverProps } from '../popover';

export type DateRangeInputProps = {
  onChange?: (startDate: Date | undefined, endDate: Date | undefined) => void;
  initialStartDate?: Date;
  initialEndDate?: Date;
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
  placeholder?: string;
  size?: 'default' | 'compact';
  onOpenChange?: (open: boolean) => void;
  startDate?: Date;
  endDate?: Date;
  align?: PopoverProps['align'];
  side?: PopoverProps['side'];
  sideOffset?: PopoverProps['sideOffset'];
  collisionBoundary?: PopoverProps['collisionBoundary'];
  onPointerDownOutside?: PopoverProps['onPointerDownOutside'];
  onEscapeKeyDown?: PopoverProps['onEscapeKeyDown'];
  portalContainer?: HTMLElement | null;
  controlledOpen?: boolean;
  onControlledOpenChange?: (open: boolean) => void;
};

const DateRangeInput = ({
  initialStartDate,
  initialEndDate,
  disableFutureDates,
  disablePastDates,
  startDate,
  endDate,
  placeholder = 'Select a date range',
  portalContainer,
  onEscapeKeyDown,
  collisionBoundary,
  align = 'start',
  side = 'bottom',
  sideOffset = 4,
  onChange,
  size,
}: DateRangeInputProps) => {
  const { t } = useTranslation('ui', { keyPrefix: 'components.date-range' });
  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>(initialStartDate);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(initialEndDate);

  useEffect(() => {
    if (startDate && endDate) {
      setSelectedStartDate(startDate);
      setSelectedEndDate(endDate);
    }
  }, [startDate, endDate]);

  const formattedRange = useMemo(
    () =>
      selectedStartDate && selectedEndDate
        ? `${format(selectedStartDate, 'MM/dd/yy')} \u2013 ${format(selectedEndDate, 'MM/dd/yy')}`
        : '',
    [selectedStartDate, selectedEndDate],
  );

  const handleRangeChange = ({
    startDate: newStartDate,
    endDate: newEndDate,
  }: {
    startDate?: Date;
    endDate?: Date;
  }) => {
    setSelectedStartDate(newStartDate);
    setSelectedEndDate(newEndDate);
    if (onChange) {
      onChange(newStartDate, newEndDate);
    }
  };

  return (
    <DateSelectorSheet
      startDate={selectedStartDate}
      endDate={selectedEndDate}
      disableFutureDates={disableFutureDates}
      disablePastDates={disablePastDates}
      onChange={handleRangeChange}
      align={align}
      side={side}
      sideOffset={sideOffset}
      portalContainer={portalContainer}
      collisionBoundary={collisionBoundary}
      onEscapeKeyDown={onEscapeKeyDown}
    >
      <button
        type="button"
        // @ts-ignore: fails for dashboard project due to i18next selector
        aria-label={t('placeholder-default')}
      >
        <Input placeholder={placeholder} size={size} value={formattedRange} readOnly />
      </button>
    </DateSelectorSheet>
  );
};

export default DateRangeInput;
